import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NUTRIENTS } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useProductVersions } from '../../data/catalog.ts'
import { useAreas, type AreaRow } from '../../data/geo.ts'
import { areaShort } from '../../data/planUi.ts'
import { fertilisationRows, seasonsOf, useApplicationAreas, useApplications } from '../../data/treatmentUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { BackLink, NotAllowed } from '../../ui/components.tsx'
import { Picto } from '../../ui/pictos.tsx'
import { LegalBanner } from './shared.tsx'

/** Mesures › "Fertilisation (kg/ha)" (docs/M15.md C; GK-029): per nutrient, per area, per season, the kg/ha the consigned treatments brought — from the
 *  DECLARED composition of the product version named on each record (product.revise@2), never a guess: a record whose version declares none is counted
 *  "non renseigné". Reads the mirror; the direction's (treatment.read). */
export function FertilisationPage() {
  const { t, i18n } = useTranslation()
  const { can, site } = useClub()
  const lang = i18n.language
  const allowed = can('treatment.read')
  const apps = useApplications(); const areas = useApplicationAreas(); const geo = useAreas(); const versions = useProductVersions()
  const seasons = useMemo(() => seasonsOf(apps ?? []), [apps])
  const thisYear = localDate(new Date(), site.timezone).slice(0, 4)
  const [season, setSeason] = useState<string | null>(null)
  const chosen = season ?? (seasons.includes(thisYear) ? thisYear : seasons[0] ?? thisYear)
  const versionsById = useMemo(() => new Map((versions ?? []).map((v) => [v.id, v])), [versions])
  const areaById = useMemo(() => new Map((geo ?? []).map((a) => [a.id, a as AreaRow])), [geo])
  const rows = useMemo(() => fertilisationRows(apps ?? [], areas ?? [], versionsById, chosen).sort((a, b) => areaShort(areaById.get(a.areaId), areaById, lang, t).localeCompare(areaShort(areaById.get(b.areaId), areaById, lang, t), lang)), [apps, areas, versionsById, chosen, areaById, lang, t])
  if (!allowed) return <section className="flex flex-col gap-4" data-testid="fertilisation-page"><h1 className="text-2xl font-bold">{t('treatment.fertilisation.title')}</h1><NotAllowed permission="treatment.read" /></section>
  if (!apps || !areas || !geo || !versions) return <p>{t('common.loading')}</p>
  return (
    <section className="flex flex-col gap-4" data-testid="fertilisation-page" data-season={chosen}>
      <BackLink to="/measures">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="leaf" className="size-7 text-brand dark:text-fairway" />{t('treatment.fertilisation.title')}</h1>
      <p className="meta">{t('treatment.fertilisation.help')}</p>
      {/* a treatment screen built from the register's rows: the legal banner stays on it like on every other (M15a review, finding 9) */}
      <LegalBanner testId="fertilisation-legal" />
      <label className="flex flex-col gap-1 font-medium"><span className="text-sm">{t('treatment.fertilisation.season')}</span>
        <select className="input" value={chosen} onChange={(e) => setSeason(e.target.value)} data-testid="fertilisation-season">{(seasons.includes(chosen) ? seasons : [chosen, ...seasons]).map((y) => <option key={y} value={y}>{y}</option>)}</select>
      </label>
      {rows.length === 0 && <p className="meta" data-testid="fertilisation-empty">{t('treatment.fertilisation.empty')}</p>}
      <ul className="flex flex-col gap-3" data-testid="fertilisation-list">
        {rows.map((r) => (
          <li key={r.areaId} className="card flex flex-col gap-1" data-testid="fertilisation-row" data-area={r.areaId}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold">{areaShort(areaById.get(r.areaId), areaById, lang, t)}</span>
              <span className="meta">{t('treatment.fertilisation.count', { count: r.applications })}{r.undeclared > 0 && <> · {t('treatment.fertilisation.undeclared', { count: r.undeclared })}</>}</span>
            </div>
            <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm sm:grid-cols-6">
              {NUTRIENTS.map((n) => <div key={n} className="flex flex-col"><dt className="meta">{n}</dt><dd className="font-semibold tabular-nums" data-nutrient={n}>{r.kgPerHa[n] === undefined ? '—' : fmtNum(Number(r.kgPerHa[n]), lang, 3)}</dd></div>)}
            </dl>
          </li>
        ))}
      </ul>
    </section>
  )
}
